import { ButtonGroup, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import LeaseModal from '../../Containers/LeaseModal';
import type { LeaseActionProps } from '../../../../shared/models/Types';
import FaqAccordion from '../../Widgets/FaqAccordion';
import DownloadButton from '../../Widgets/DownloadButton';
import LeaseExtension from '../../../models/LeaseExtension';
import { useLeasingUrlParams } from '../../../hooks';

type Props = LeaseActionProps;

export const FAQ_SECTIONS = [
  {
    label: 'What happens next?',
    description: (
      <Text>
        After you share the lease renewal agreement with your renters, check with them to see if all the terms are
        acceptable. Make any changes here in Zillow Rental Manager before re-sharing the lease renewal agreement. Once
        your lease renewal agreement is final, request signatures to start the signing process.
      </Text>
    ),
  },
  {
    label: 'How can my renters and I sign this lease renewal agreement?',
    description: (
      <Text>Once your lease renewal agreement is final, request signatures from your leases dashboard.</Text>
    ),
  },
  {
    label: 'How do I share this draft with people who aren’t my tenants, such as my lawyer?',
    description: (
      <Text>
        Once you download the draft PDF, you can email or text them the file. If you prefer, you can also print a copy
        and share it via regular mail or in person.
      </Text>
    ),
  },
  {
    label: 'How else can I make sure this lease renewal agreement is valid?',
    description: (
      <>
        <Text>
          First off, do not handwrite changes on a printed copy of the lease renewal agreement. If you need to make
          changes, do that here in Zillow Rental Manger then re-share the lease renewal agreement with your tenants.
        </Text>
      </>
    ),
  },
];

export default function OpenModalDownloadShareDraft({ terminateActiveAction, lease }: Props): JSX.Element {
  const { renewalId } = useLeasingUrlParams();
  const latestExtension = lease?.latestExtension ?? null;
  const extensionId = renewalId || latestExtension?.id || '';

  return (
    <LeaseModal
      header={<Heading level={1}>Download/share draft</Heading>}
      onClose={terminateActiveAction}
      isOpen
      body={
        <>
          <Paragraph css={{ marginBottom: 'tight' }}>
            You are about to download the latest draft of your lease renewal agreement. The file isn’t meant to be
            signed. It’s for sharing with your tenants for review. Email or text them the downloaded file.
          </Paragraph>
          <FaqAccordion title="You may be wondering…" sections={FAQ_SECTIONS} />
        </>
      }
      footer={
        <ButtonGroup aria-label="download draft">
          <TextButton tone="brand" onClick={terminateActiveAction}>
            Cancel
          </TextButton>
          <DownloadButton
            url={LeaseExtension.getPDFLink(extensionId, true)}
            buttonType="primary"
            buttonLabel="Download draft PDF"
            onDownloadSuccess={terminateActiveAction}
          />
        </ButtonGroup>
      }
    />
  );
}
